/**
 * usePlanSaving - Hook for managing all plan saving operations
 *
 * This hook consolidates all saving-related functionality for meal plans,
 * handling both virtual and saved plans with proper confirmation flows.
 *
 * Features:
 * - Virtual plan to real plan conversion
 * - Dietitian confirmation dialogs
 * - Save state management
 * - Auto-save to local storage
 * - Permission-aware saving
 * - Error handling and recovery
 */

import { useCallback, useState } from 'react';
import { createNewDocument, updateCachedDocuments } from '@utils/Content';
import { fetchVirtualPlan, deleteVirtualPlan } from '@utils/Plans';
import { getConfig } from '@utils/Env';
import AuthStore from '@stores/AuthStore';
import store from 'store';
import { addRecentlyEditedPlan, removeRecentlyEditedPlan } from '@pro/utils/Recent';
import { updateEditedCopy, clearEditedCopy } from '../utils/localStorageManager';
import { useSaveState } from './useSaveState';
import confirm from '@utils/Confirm';
import BoardStore from '@stores/BoardStore';
import BoardActions from '@actions/BoardActions';
import Analytics from '@utils/Analytics';

/**
 * Configuration for plan saving
 * @typedef {Object} PlanSavingConfig
 * @property {Object} plan - Current plan data
 * @property {Object} virtualPlanHandler - Virtual plan handler from useVirtualPlan
 * @property {Object} permissions - Plan permissions
 * @property {Object} profile - User profile
 * @property {Object} user - User object
 * @property {boolean} isDietitianRecommended - Whether plan is dietitian recommended
 * @property {boolean} autoSave - Enable auto-save to local storage
 * @property {number} autoSaveDelay - Auto-save delay in ms
 * @property {Function} onPlanSaved - Callback when plan is successfully saved with new plan data
 */

/**
 * Hook for managing plan saving operations
 *
 * @param {PlanSavingConfig} config - Configuration object
 * @returns {Object} Saving functions and state
 */
export const usePlanSaving = (config = {}) => {
    const {
        plan = null,
        virtualPlanHandler = null,
        permissions = {},
        profile = {},
        user = null,
        isDietitianRecommended = false,
        autoSave = false,
        autoSaveDelay = 3000,
        onPlanSaved = null,
    } = config;

    // Save state management
    const saveState = useSaveState({
        autoSave: false, // We handle auto-save manually for local storage
        autoSaveDelay,
        isVirtualPlan: virtualPlanHandler?.isVirtual || false,
    });

    // Duplicate state (separate from save state)
    const [isDuplicating, setIsDuplicating] = useState(false);

    /**
     * Add plan to favorites automatically after save (like legacy editor)
     * Includes the profile data so Plan Settings can display correct info when reopened
     */
    const addPlanToFavorites = useCallback((savedPlan) => {
        const lastBoard = BoardStore.getDefaultBoard();
        const newItem = {
            resource_id: savedPlan.uuid,
            resource_type: savedPlan.type || 'plan',
        };

        // Include profile data so it persists with the favorite
        if (profile && Object.keys(profile).length > 0) {
            newItem.profile = profile;
        }

        if (lastBoard.links) {
            // Board already exists, just add to it.
            BoardActions.addToBoard(lastBoard, [newItem]);
        } else {
            lastBoard.contents = lastBoard.contents || [];
            lastBoard.contents.push(newItem);
            // Board does not exist, create a whole new one
            BoardActions.upsertBoards([lastBoard]);
        }
    }, [profile]);

    /**
     * Auto-save plan to local storage (not server)
     */
    const autoSaveToLocal = useCallback((planData) => {
        if (!isDietitianRecommended && planData) {
            setTimeout(() => {
                // Use setTimeout to ensure the state update has completed
                updateEditedCopy(planData, user?.patient || null);
            }, 0);
        }
    }, [isDietitianRecommended, user]);

    /**
     * Perform save operation - handles both virtual and real plans
     */
    const performSave = useCallback(async (planData) => {
        const isVirtual = virtualPlanHandler?.isVirtual;
        let response;

        if (isVirtual) {
            // Virtual plan - use createNewDocument flow
            const { uuid, links, created, type, status, merchant, ...rest } = planData;

            response = await createNewDocument('plan', rest, 'plan/1');

            // Clean up virtual plan and update cache
            deleteVirtualPlan(uuid, response.uuid);
            store.remove('menu-edited-' + uuid);
            updateCachedDocuments([response]);

            // Handle recently edited plans
            removeRecentlyEditedPlan(uuid);
            addRecentlyEditedPlan(response);

        } else {
            // Existing plan - use regular POST
            const { uuid, links, type, status, ...rest } = planData;

            // Validate that we have a proper save URL
            if (!planData.links?.self?.href) {
                throw new Error('Cannot save plan: missing or invalid save URL (links.self.href)');
            }

            const relativePath = planData.links.self.href;
            const saveUrl = getConfig('recipe_api') + relativePath;

            response = await AuthStore.fetch(
                saveUrl,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json; schema=plan/1',
                        'Accept': 'application/json; schema=plan/1',
                    },
                    body: JSON.stringify(rest)
                },
                true
            );

            // Clean up localStorage for existing plan
            clearEditedCopy(uuid);

            // Handle recently edited plans
            if (response?.plan) {
                addRecentlyEditedPlan(response.plan);
            }
        }

        return { success: true, plan: response };
    }, [virtualPlanHandler]);

    /**
     * Check if this plan belongs to another user (requires copying)
     */
    const shouldCopyPlan = useCallback(() => {
        return plan?.owner !== profile.uuid;
    }, [plan, profile]);

    /**
     * Internal save function (used by both direct save and confirm dialog)
     */
    const performSavePlan = useCallback(async () => {
        if (!plan) {
            return { success: false, error: 'No plan to save' };
        }

        // Check if this is a virtual plan (no links.self means it's virtual)
        const isVirtualPlan = !plan?.links?.self;

        if (!permissions.canWrite && !permissions.canCreate && !isVirtualPlan) {
            return { success: false, error: 'No write permission' };
        }

        // Set saving state
        saveState.setSaving(true);

        try {
            // Use performSave directly - let any errors bubble up
            const result = await performSave(plan);

            // Mark save as successful (this triggers success message and justSaved automatically)
            saveState.markSaveSuccess();

            // Check if UUID changed (virtual plan conversion or duplication)
            const oldUuid = plan.uuid;
            const newUuid = result.plan?.uuid;

            if (onPlanSaved && result.plan) {
                onPlanSaved(result.plan);
            }

            // Fire analytics event
            if (result.plan) {
                Analytics.saveMealPlan(result.plan, {
                    'Source': 'Meal Plan Editor',
                    'Plan Type': isVirtualPlan ? 'Virtual' : 'Existing',
                });
            }

            // Automatically add to favorites after save (like legacy editor)
            if (result.plan) {
                addPlanToFavorites(result.plan);
            }

            return result;
        } catch (error) {
            saveState.markSaveError(error);
            return { success: false, error: error.message };
        }
    }, [plan, permissions.canWrite, performSave, saveState, onPlanSaved, addPlanToFavorites]);

    /**
     * Save plan (public interface with confirm dialog for dietitians)
     */
    const savePlan = useCallback(async () => {
        // Check permissions before doing anything
        const isVirtualPlan = !plan?.links?.self;
        if (!permissions.canWrite && !permissions.canCreate && !isVirtualPlan) {
            return { success: false, error: 'No write permission' };
        }

        // Check if we need to show the confirm dialog for dietitians
        if (profile?.dietitian && !shouldCopyPlan()) {
            const practiceType = profile.practice_type || 'dietetics';
            const clientTerm = practiceType === 'dietetics' ? 'patients' : 'clients';

            const shouldProceed = await confirm(
                <p>
                    <strong>
                        If you've previously recommended this plan to one or more {clientTerm},
                        any modifications will be visible to those {clientTerm}.
                    </strong>
                    <br/>
                    <br/>
                    To prevent this, please create a duplicate of the plan and make edits there.
                    Do you want to proceed with saving your changes?
                </p>,
                {
                    acceptText: 'Yes, Save',
                    rejectText: 'No, Cancel'
                }
            );

            if (!shouldProceed) {
                return { success: false, cancelled: true };
            }
        }

        // Proceed with the actual save
        return performSavePlan();
    }, [profile, shouldCopyPlan, performSavePlan, plan, permissions]);

    /**
     * Duplicate plan - creates a copy with new UUID
     */
    const duplicatePlan = useCallback(async () => {
        if (!plan) {
            return { success: false, error: 'No plan to duplicate' };
        }

        // Can only duplicate saved plans (not virtual)
        if (!plan?.links?.self) {
            return { success: false, error: 'Cannot duplicate unsaved plan' };
        }

        // Strip out fields that should not be copied
        const { uuid, links, created, type, status, merchant, ...rest } = plan;

        // Set owner to current user
        rest.owner = user?.uuid;

        // Add "Copy - " prefix to title
        const planTitle = rest.title || 'Meal Plan';
        if (!planTitle.startsWith('Copy - ')) {
            rest.title = 'Copy - ' + planTitle;
        } else {
            rest.title = planTitle;
        }

        // Set duplicating state (separate from save state)
        setIsDuplicating(true);

        try {
            // Create new document
            const response = await createNewDocument('plan', rest, 'plan/1');

            // Update cache
            updateCachedDocuments([response]);

            // Fire analytics event
            Analytics.saveMealPlan(response, {
                'Source': 'Meal Plan Editor',
                'Plan Type': 'Duplicate',
            });

            // Automatically add to favorites
            addPlanToFavorites(response);

            // Notify parent component (this will navigate to the new plan)
            if (onPlanSaved && response) {
                try {
                    onPlanSaved(response);
                } catch (callbackError) {
                    setIsDuplicating(false);
                    return { success: false, error: callbackError.message };
                }
            }

            // Reset state after a short delay to allow navigation
            setTimeout(() => {
                setIsDuplicating(false);
            }, 1000);

            return { success: true, plan: response };
        } catch (error) {
            setIsDuplicating(false);
            return { success: false, error: error.message };
        }
    }, [plan, user, onPlanSaved, addPlanToFavorites]);

    return {
        // Save functions
        savePlan,
        duplicatePlan,
        performSave,
        autoSaveToLocal,

        // Save state (includes justSaved)
        saveState,

        // Duplicate state
        isDuplicating,

        // Utilities
        shouldCopyPlan,

        // Computed values
        canSave: plan && (permissions.canWrite || !plan?.links?.self), // Can save if has write permission or is virtual plan
        isSaving: saveState.isSaving,
        isDirty: saveState.isDirty,
        lastSaveTime: saveState.lastSaveTime,
        saveError: saveState.lastError
    };
};
